import {
  CircleOffIcon,
  CircleX,
  FileText,
  ForwardIcon,
  GitPullRequestClosed,
  Loader,
  Route as RouteIcon,
  Ticket as TicketIcon,
  Timer as TimerIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

import {
  type AiIncidentData,
  calculateFlightDelta,
  finalArrivalDeltaMinutes,
  formatSignedDeltaMinutes,
  getSequenceLabel,
  groupBySequence,
} from "../../lib/incident-delta";
import { Markdown } from "../../lib/markdown";
import { AiIncidentFlightCard } from "./ai-incident-flight-card";

/**
 * Two-column "Réservation prévue" vs "Itinéraire Final" comparison of an
 * AI-extracted incident, with sequence selection and the "Confirmer l'incident"
 * block. Port of AiIncidentPreview.vue.
 */

export type IncidentCause = "CANCELLED" | "DELAYED" | "DIVERTED" | "OVERBOOKED";
export type IncidentConsequence =
  | "DEPARTURE_ADVANCED_BY_MORE_THAN_1_HOUR"
  | "ARRIVAL_AT_FINAL_DESTINATION_DELAYED_BY_MORE_THAN_3_HOURS"
  | "MISSED_CONNECTION"
  | "NO_VALID_REROUTING_OFFERED"
  | "JOURNEY_FULLY_CANCELLED";

export const CAUSE_OPTIONS: Array<{ value: IncidentCause; label: string }> = [
  { value: "CANCELLED", label: "Annulation" },
  { value: "DELAYED", label: "Retard" },
  { value: "DIVERTED", label: "Vol dérouté" },
  { value: "OVERBOOKED", label: "Sur-booking" },
];

/**
 * Persisted claim types (claim.claimType.name) that are a *delay* — the single
 * "Retard" card layout and the per-card "Retard de …" line are gated on these,
 * not on the AI-guessed incident cause (which is user-overridable and defaults
 * to CANCELLED). A cancellation/overbooking claim must never render as a delay.
 */
export const DELAY_CLAIM_TYPES = new Set(["FLIGHT_DELAY", "DELAY_FROM_PNR_SCHEDULE"]);

export const CONSEQUENCE_OPTIONS: Array<{ value: IncidentConsequence; label: string }> = [
  { value: "DEPARTURE_ADVANCED_BY_MORE_THAN_1_HOUR", label: "Départ avancé > 1H" },
  {
    value: "ARRIVAL_AT_FINAL_DESTINATION_DELAYED_BY_MORE_THAN_3_HOURS",
    label: "Arrivée à destination finale > 3H",
  },
  { value: "MISSED_CONNECTION", label: "Rupture de correspondance" },
  { value: "NO_VALID_REROUTING_OFFERED", label: "Pas de ré-acheminement valable proposé par la compagnie" },
  { value: "JOURNEY_FULLY_CANCELLED", label: "Reste du voyage complètement annulé" },
];

export interface AiIncidentPreviewConfirmData {
  cause: IncidentCause;
  consequence: IncidentConsequence;
  /** The sequence the jurist marked as impacted. */
  sequence: number;
}

interface AiIncidentPreviewProps {
  incident: AiIncidentData;
  loading?: boolean;
  showValidate?: boolean;
  onConfirm?: (data: AiIncidentPreviewConfirmData) => void;
  /** Extra actions next to « Confirmer l'incident » (e.g. the abandon button). */
  confirmActions?: ReactNode;
  /** Ghost when confirming goes against the AI verdict and the abandon action is the red one. */
  confirmVariant?: "default" | "ghost";
  /** Persisted claim type (claim.claimType.name) — gates the "Retard" delay display. */
  claimType?: string;
  /** "Compréhension du dossier" panel — rendered after the comparison, before "Confirmer l'incident". */
  toolbox?: ReactNode;
  /** "Billets électroniques" section (rich per-passenger TicketHistory); simple list used when absent. */
  eticketsSection?: ReactNode;
  /** Extraordinary circumstances section (injected after e-tickets). */
  cxSection?: ReactNode;
}

export function AiIncidentPreview({
  incident,
  loading = false,
  showValidate = false,
  onConfirm,
  confirmActions,
  confirmVariant = "default",
  claimType,
  toolbox,
  eticketsSection,
  cxSection,
}: AiIncidentPreviewProps) {
  const originalFlights = incident?.incident?.itinerary?.original ?? [];
  const actualFlights = incident?.incident?.itinerary?.actual ?? [];

  // Sequence selection
  const availableSequences = [...new Set(originalFlights.map((flight) => flight.sequence))].sort(
    (a, b) => a - b,
  );

  // Default to the sequence that AI marked as impacted; if not exactly one, fall back to the first.
  const impactedSequences = new Set(
    originalFlights.filter((flight) => flight.isImpactedSequence).map((flight) => flight.sequence),
  );
  const defaultImpactedSequence =
    impactedSequences.size === 1 ? [...impactedSequences][0] : (availableSequences[0] ?? 1);

  const [selectedSequence, setSelectedSequence] = useState(defaultImpactedSequence);

  // Cause / consequence selection (AI values preselected)
  const [selectedCause, setSelectedCause] = useState<IncidentCause>(
    (incident?.incident?.cause as IncidentCause) || CAUSE_OPTIONS[0].value,
  );
  const [selectedConsequence, setSelectedConsequence] = useState<IncidentConsequence>(
    (incident?.incident?.consequence as IncidentConsequence) || CONSEQUENCE_OPTIONS[0].value,
  );

  // The selected sequence is the only one shown (the Vue version rewrote
  // isImpactedSequence in place; here we derive it from local state).
  const impactedOriginal = originalFlights.filter((flight) => flight.sequence === selectedSequence);
  const impactedActual = actualFlights
    .filter((flight) => flight.sequence === selectedSequence)
    .map((flight) => {
      const originalFlight = originalFlights.find(
        (candidate) => candidate.sequence === flight.sequence && candidate.order === flight.order,
      );
      return {
        ...flight,
        delta: originalFlight ? calculateFlightDelta(flight, originalFlight) : undefined,
      };
    });

  const groupedOriginal = groupBySequence(impactedOriginal);
  const groupedActual = groupBySequence(impactedActual);
  const hasActualItinerary = impactedActual.length > 0;

  // Retard : les vols sont les mêmes des deux côtés — une seule carte
  // (horaires prévus barrés + réels + delta) remplace la double colonne. Gated on
  // the *persisted* claim type, not the AI-guessed cause (TECH-705).
  const isDelayClaim = !!claimType && DELAY_CLAIM_TYPES.has(claimType);
  const isDelayLayout = isDelayClaim && hasActualItinerary;

  // Deltas clés (juste après la comparaison) : arrivée à destination finale, et départ du premier
  // aéroport quand le vol a été avancé (delta négatif d'au moins 1h, ou conséquence "départ avancé").
  const arrivalDeltaMinutes = finalArrivalDeltaMinutes(impactedActual, impactedOriginal);
  const departureDeltaMinutes = impactedActual[0]?.delta?.departure ?? null;
  // Strictly more than 1h early — the EU261 "départ avancé > 1H" threshold (same as the flight
  // card). The dedicated delta frame is gated on this measured threshold only: a hand-picked
  // "Départ avancé > 1H" consequence with a 7-min advance must not surface a départ delta.
  const hasAdvancedDeparture = departureDeltaMinutes != null && departureDeltaMinutes < -60;

  // A detected advanced departure (>1h) enforces the matching consequence in the confirm select —
  // the jurist can still override it afterwards (the effect only re-fires when detection flips).
  useEffect(() => {
    if (hasAdvancedDeparture) {
      setSelectedConsequence("DEPARTURE_ADVANCED_BY_MORE_THAN_1_HOUR");
    }
  }, [hasAdvancedDeparture]);

  const notes = incident?.incident?.notes;
  const etickets = incident?.etickets ?? [];

  function handleConfirm() {
    onConfirm?.({ cause: selectedCause, consequence: selectedConsequence, sequence: selectedSequence });
  }

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-1 text-3xl font-bold leading-tight tracking-tight text-slate-800">
            Comparaison d'Itinéraire
          </h1>
          <p className="text-sm text-slate-500">Incident extrait par IA</p>
        </div>

        {/* Claim summary (context.summary) */}
        {notes ? (
          <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <FileText className="h-5 w-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-slate-800">Résumé de l'incident</h3>
            </div>
            <Markdown text={notes} className="max-w-none text-sm text-gray-700" />
          </div>
        ) : null}

        {/* Sequence Selection (only show when multiple sequences exist) */}
        {availableSequences.length > 1 ? (
          <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <RouteIcon className="h-5 w-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-slate-800">Séquence impactée</h3>
            </div>
            <div className="flex gap-4">
              {availableSequences.map((seq) => (
                <label
                  key={seq}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-lg border-2 px-4 py-3 transition-colors",
                    selectedSequence === seq
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                  )}
                >
                  <input
                    type="radio"
                    value={seq}
                    checked={selectedSequence === seq}
                    onChange={() => setSelectedSequence(seq)}
                    className="h-4 w-4 text-indigo-600"
                  />
                  <span className="font-medium">
                    {getSequenceLabel(seq)} (Séquence {seq})
                  </span>
                </label>
              ))}
            </div>
          </div>
        ) : null}

        {/* Retard : carte unique (prévu + réel + delta), sinon comparaison 2 colonnes */}
        {isDelayLayout ? (
          <div className="mx-auto max-w-3xl space-y-6 px-4">
            <div className="mb-6 flex items-center gap-3">
              <div className="h-3 w-3 rounded-full bg-amber-500" />
              <h2 className="text-xl font-semibold tracking-tight text-slate-800">Retard</h2>
              <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700">
                {impactedActual.length} vols
              </span>
            </div>

            {groupedActual.map(([sequenceNum, sequence]) => (
              <div key={sequenceNum} className="space-y-4">
                {/* Sequence Header */}
                <div className="mb-4 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-white">
                    {sequenceNum}
                  </div>
                  <span className="font-medium text-slate-700">{getSequenceLabel(sequence[0].sequence)}</span>
                </div>

                {/* Flights in sequence */}
                <div className="overflow-hidden rounded-3xl border-4 border-amber-200 bg-white shadow-sm">
                  {sequence.map((flight, index) => (
                    <AiIncidentFlightCard
                      // biome-ignore lint/suspicious/noArrayIndexKey: ordered static list
                      key={index}
                      flight={flight}
                      nextFlight={sequence[index + 1]}
                      type="actual"
                      isFirst={index === 0}
                      isLast={index === sequence.length - 1}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-8 px-4 lg:grid-cols-2">
            {/* Original Itinerary */}
            <div className="space-y-6">
              <div className="mb-6 flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-blue-500" />
                <h2 className="text-xl font-semibold tracking-tight text-slate-800">Réservation prévue</h2>
                <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
                  {impactedOriginal.length} vols
                </span>
              </div>

              {groupedOriginal.map(([sequenceNum, sequence]) => (
                <div key={sequenceNum} className="space-y-4">
                  {/* Sequence Header */}
                  <div className="mb-4 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
                      {sequenceNum}
                    </div>
                    <span className="font-medium text-slate-700">
                      {getSequenceLabel(sequence[0].sequence)}
                    </span>
                  </div>

                  {/* Flights in sequence */}
                  <div className="overflow-hidden rounded-3xl border-4 border-blue-200 bg-white shadow-sm">
                    {sequence.map((flight, index) => (
                      <AiIncidentFlightCard
                        // biome-ignore lint/suspicious/noArrayIndexKey: ordered static list
                        key={index}
                        flight={flight}
                        nextFlight={sequence[index + 1]}
                        type="original"
                        isFirst={index === 0}
                        isLast={index === sequence.length - 1}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Actual Itinerary */}
            <div className="space-y-6">
              <div className="mb-6 flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-amber-500" />
                <h2 className="text-xl font-semibold tracking-tight text-slate-800">Itinéraire Final</h2>
                <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700">
                  {impactedActual.length} vols
                </span>
              </div>

              {hasActualItinerary
                ? groupedActual.map(([sequenceNum, sequence]) => (
                    <div key={sequenceNum} className="space-y-4">
                      {/* Sequence Header */}
                      <div className="mb-4 flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-white">
                          {sequenceNum}
                        </div>
                        <span className="font-medium text-slate-700">
                          {getSequenceLabel(sequence[0].sequence)}
                        </span>
                      </div>

                      {/* Flights in sequence */}
                      <div className="overflow-hidden rounded-3xl border-4 border-amber-200 bg-white shadow-sm">
                        {sequence.map((flight, index) => (
                          <AiIncidentFlightCard
                            // biome-ignore lint/suspicious/noArrayIndexKey: ordered static list
                            key={index}
                            flight={flight}
                            nextFlight={sequence[index + 1]}
                            type="actual"
                            isFirst={index === 0}
                            isLast={index === sequence.length - 1}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                : /* Empty state for no rerouting (with sequence headers) */
                  groupedOriginal.map(([sequenceNum, sequence]) => (
                    <div key={sequenceNum} className="space-y-4">
                      {/* Sequence Header */}
                      <div className="mb-4 flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-white">
                          {sequenceNum}
                        </div>
                        <span className="font-medium text-slate-700">
                          {getSequenceLabel(sequence[0].sequence)}
                        </span>
                      </div>

                      {/* Empty state card */}
                      <div className="overflow-hidden rounded-3xl border-4 border-amber-200 bg-white p-8 text-center shadow-sm">
                        <CircleOffIcon className="mx-auto mb-3 h-12 w-12 text-slate-400" />
                        <p className="font-medium text-slate-600">Aucun réacheminement accepté</p>
                        <p className="mt-1 text-sm text-slate-500">Le passager n'a pas été ré-acheminé</p>
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        )}

        {/* Information cruciale : delta départ quand le vol a été avancé (>1h), sinon delta à
            destination finale — jamais les deux, le départ avancé est le fait décisif.
            A 0-min arrival delta (on-time reroute after a cancellation) is hidden — it isn't a delay. */}
        {(hasAdvancedDeparture && departureDeltaMinutes != null) || !!arrivalDeltaMinutes ? (
          <div className="mx-4 mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            {hasAdvancedDeparture && departureDeltaMinutes != null ? (
              <div className="flex flex-wrap items-center gap-3">
                <TimerIcon className="h-5 w-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-slate-800">Delta départ (premier départ) :</h3>
                <span className="inline-flex items-center gap-1.5 text-lg font-bold text-rose-700">
                  {formatSignedDeltaMinutes(departureDeltaMinutes)}
                  {departureDeltaMinutes < 0 ? (
                    <>
                      <CircleX className="h-5 w-5 flex-none text-rose-500" />
                      <span className="text-sm font-semibold">vol avancé</span>
                    </>
                  ) : null}
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <TimerIcon className="h-5 w-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-slate-800">
                  Delta arrivée (à destination finale) :
                </h3>
                <span
                  className={cn(
                    "text-lg font-bold",
                    (arrivalDeltaMinutes ?? 0) > 0 ? "text-red-800" : "text-slate-700",
                  )}
                >
                  {formatSignedDeltaMinutes(arrivalDeltaMinutes ?? 0)}
                  {(arrivalDeltaMinutes ?? 0) < 0 ? " (arrivée avancée)" : ""}
                </span>
              </div>
            )}
          </div>
        ) : null}

        {/* Compréhension du dossier (assessment toolbox) — sits before the confirmation block */}
        {toolbox}

        {/* Confirmer l'incident — after the case understanding, in confirmation mode */}
        {showValidate ? (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <GitPullRequestClosed className="h-5 w-5 text-amber-500" />
              <h3 className="text-lg font-semibold text-slate-800">Confirmer l'incident</h3>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Left Column - Cause Selection */}
              <div className="rounded-xl bg-blue-50 p-4 text-center">
                <div className="text-2xl font-bold text-slate-800">1</div>
                <div className="text-left">
                  <Label className="mb-1 block text-sm font-medium text-gray-700">
                    Sélectionner la cause
                  </Label>
                  <Select
                    value={selectedCause}
                    onValueChange={(value) => {
                      if (value) {
                        setSelectedCause(value as IncidentCause);
                      }
                    }}
                  >
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue>
                        {CAUSE_OPTIONS.find((option) => option.value === selectedCause)?.label}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {CAUSE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Right Column - Consequence Selection */}
              <div className="rounded-xl bg-emerald-50 p-4 text-center">
                <div className="text-2xl font-bold text-emerald-600">2</div>
                <div className="text-left">
                  <Label className="mb-1 block text-sm font-medium text-gray-700">Type de conséquence</Label>
                  <Select
                    value={selectedConsequence}
                    onValueChange={(value) => {
                      if (value) {
                        setSelectedConsequence(value as IncidentConsequence);
                      }
                    }}
                  >
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue>
                        {CONSEQUENCE_OPTIONS.find((option) => option.value === selectedConsequence)?.label}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {CONSEQUENCE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Confirm Button */}
            <div className="mb-8 mt-12 flex justify-center gap-3">
              <Button size="lg" variant={confirmVariant} onClick={handleConfirm} disabled={loading}>
                {loading ? <Loader className="animate-spin" /> : <ForwardIcon />}
                Confirmer l'incident
              </Button>
              {confirmActions}
            </div>
          </div>
        ) : null}

        {/* Billets électroniques — rich per-passenger TicketHistory when injected, else a simple list */}
        {eticketsSection ?? (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold tracking-tight text-slate-800">
              <TicketIcon className="h-5 w-5 text-blue-600" />
              Billets électroniques
            </h4>
            {etickets.length > 0 ? (
              <div className="space-y-2">
                {etickets.map((eticket, index) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static list
                    key={index}
                    className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm font-medium text-slate-700">{eticket.eticket}</span>
                      {eticket.passenger ? (
                        <span className="text-sm text-slate-500">{eticket.passenger}</span>
                      ) : null}
                    </div>
                    <span className="text-xs text-slate-500">#{eticket.amadeusRecordNumber}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm italic text-slate-500">Aucun billet électronique.</p>
            )}
          </div>
        )}

        {/* Extraordinary circumstances (or explicit empty state) */}
        {cxSection}
      </div>
    </div>
  );
}
